const PRELOAD_RECOVERY_KEY = 'tpb:preload-recovery-at';
const PRELOAD_RECOVERY_COOLDOWN_MS = 30_000;

function readRecoveryTime(storage: Storage): number {
  const value = Number(storage.getItem(PRELOAD_RECOVERY_KEY));
  return Number.isFinite(value) ? value : 0;
}

/**
 * Recovers stale tabs after a deployment replaces lazily loaded route chunks.
 * Vite emits this event before the failed dynamic import reaches React.
 */
export function installNavigationRecovery(target: Window = window): () => void {
  let clearMarkerTimer: number | undefined;

  try {
    const elapsed = Date.now() - readRecoveryTime(target.sessionStorage);
    if (elapsed >= PRELOAD_RECOVERY_COOLDOWN_MS) {
      target.sessionStorage.removeItem(PRELOAD_RECOVERY_KEY);
    } else {
      clearMarkerTimer = target.setTimeout(() => {
        target.sessionStorage.removeItem(PRELOAD_RECOVERY_KEY);
      }, PRELOAD_RECOVERY_COOLDOWN_MS - elapsed);
    }
  } catch {
    // Storage can be disabled. Recovery still works without the loop guard.
  }

  const handlePreloadError = (event: Event) => {
    const now = Date.now();

    try {
      const lastRecovery = readRecoveryTime(target.sessionStorage);
      if (now - lastRecovery < PRELOAD_RECOVERY_COOLDOWN_MS) {
        return;
      }
      target.sessionStorage.setItem(PRELOAD_RECOVERY_KEY, String(now));
    } catch {
      // Storage can be disabled. Allow the automatic recovery attempt.
    }

    event.preventDefault();
    target.location.reload();
  };

  target.addEventListener('vite:preloadError', handlePreloadError);

  return () => {
    target.removeEventListener('vite:preloadError', handlePreloadError);
    if (clearMarkerTimer !== undefined) {
      target.clearTimeout(clearMarkerTimer);
    }
  };
}
